import * as React from "react";
import { useBlocker, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Eye, FileText, Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/Combobox";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { SummaryRow } from "@/components/SummaryRow";
import { BillsSummaryCard, NetPayableRows } from "@/components/requests/DetailCard";
import { CurrencyConversionCard } from "@/components/requests/CurrencyConversionCard";
import { DocumentsStep } from "@/components/requests/DocumentsStep";
import { ExpensesStep } from "@/components/requests/ExpensesStep";
import { FinanceNoticeDialog } from "@/components/requests/FinanceNoticeDialog";
import { OtherLinkAdvanceField } from "@/components/requests/OtherLinkAdvanceField";
import { WizardStepper } from "@/components/requests/WizardStepper";
import { formatAdvanceAmounts, netPayableByCurrency } from "@/lib/advance";
import { COUNTRIES, currencyForCountry } from "@/lib/countries";
import { spansMultipleCurrencies } from "@/lib/fx";
import {
  ApiError,
  createMyOtherRequest,
  fetchAdvance,
  fetchFinanceApprovers,
  fetchProfile,
  fetchTravelRequest,
  finalizeMyRequest,
  FinanceApprover,
  OtherRequestHeader,
  resubmitMyOtherRequest,
  setOtherLinkedAdvance,
  updateMyOtherRequest
} from "../../api";
import {
  Advance,
  AdvanceAmount,
  ExpenseLineItem,
  FxOverride,
  OTHER_CLAIM_TYPE_LABEL,
  OTHER_CLAIM_TYPES,
  OtherClaimType,
  RequestDocument
} from "../../types";

// Claim Type → Advance? → Bills → Review & Submit. Same flow as the travel
// wizard (NewRequest.tsx) minus Travel Details: the country, purpose and
// Finance Approver are asked for at the top of the Bills step instead, and
// the Draft is created once they're filled in (bills need a request to
// attach to). Everything after that - bills, finalize, approvals, the
// reconciliation ledger, settlement - reuses the travel request's own
// routes and components.
const STEPS = [
  { label: "Claim Type" },
  { label: "Advance?" },
  { label: "Bills" },
  { label: "Review & Submit" }
];
const CLAIM_TYPE_STEP = 1;
const ADVANCE_STEP = 2;
const BILLS_STEP = 3;
const REVIEW_STEP = 4;

const CLAIM_TYPE_HINT: Record<OtherClaimType, string> = {
  city_travel: "Local trips within a city - cab, auto, metro and the like.",
  miscellaneous: "Anything else spent for work - stationery, courier, supplies."
};

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.name, label: c.name }));

interface Header {
  country: string;
  currency: string;
  purpose: string;
  financeApproverId: string;
}

const EMPTY_HEADER: Header = { country: "", currency: "", purpose: "", financeApproverId: "" };

export default function NewOtherRequest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdToResume = searchParams.get("draftId");
  const isResubmit = searchParams.get("resubmit") === "1";

  const [step, setStep] = React.useState(CLAIM_TYPE_STEP);
  const [claimType, setClaimType] = React.useState<OtherClaimType | "">("");
  const [linkedAdvanceId, setLinkedAdvanceId] = React.useState<string | null>(null);
  const [linkedAdvanceAmounts, setLinkedAdvanceAmounts] = React.useState<AdvanceAmount[] | null>(null);
  // The "Did you already receive an advance?" toggle shown by
  // OtherLinkAdvanceField - owned here, not by that component, so it
  // survives OtherLinkAdvanceField unmounting when the employee leaves the
  // Advance? step and comes back (it's only rendered while `step ===
  // ADVANCE_STEP`). Lets validateStep block Next on that step while it's on
  // but nothing's been picked yet.
  const [advanceToggleOn, setAdvanceToggleOn] = React.useState(false);
  const [header, setHeader] = React.useState<Header>(EMPTY_HEADER);
  // What the server has - header edits after the Draft exists are saved
  // (PUT) only when they differ from this.
  const [savedHeader, setSavedHeader] = React.useState<Header>(EMPTY_HEADER);

  const [draftRequestId, setDraftRequestId] = React.useState<string | null>(null);
  const [draftCreatedAt, setDraftCreatedAt] = React.useState<string | null>(null);
  const [expenses, setExpenses] = React.useState<ExpenseLineItem[]>([]);
  const [documents, setDocuments] = React.useState<RequestDocument[]>([]);
  const [fxOverrides, setFxOverrides] = React.useState<FxOverride[]>([]);
  const [settlementTargetByCurrency, setSettlementTargetByCurrency] = React.useState<Record<string, string>>({});

  const [expensesBusy, setExpensesBusy] = React.useState(false);
  const [documentsBusy, setDocumentsBusy] = React.useState(false);
  const [unaddedBills, setUnaddedBills] = React.useState(0);
  const [editingExpense, setEditingExpense] = React.useState(false);

  const [creatingDraft, setCreatingDraft] = React.useState(false);
  const [savingHeader, setSavingHeader] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resuming, setResuming] = React.useState(!!draftIdToResume);
  const [resubmitNote, setResubmitNote] = React.useState<{ stage: "Manager" | "Finance"; comment: string } | null>(null);
  const [viewingBills, setViewingBills] = React.useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = React.useState(false);
  const [financeNoticeOpen, setFinanceNoticeOpen] = React.useState(false);
  const [financeCc, setFinanceCc] = React.useState<string[]>([]);

  const [designation, setDesignation] = React.useState("");
  const [hasReportingManager, setHasReportingManager] = React.useState(true);
  const [reportingManagerName, setReportingManagerName] = React.useState<string | null>(null);
  const [financeApprovers, setFinanceApprovers] = React.useState<FinanceApprover[]>([]);
  const [financeApproverNameAtSubmission, setFinanceApproverNameAtSubmission] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchFinanceApprovers()
      .then(setFinanceApprovers)
      .catch(() => setFinanceApprovers([]));
    fetchProfile()
      .then((profile) => {
        if (profile.jobTitle) setDesignation(profile.jobTitle);
        setHasReportingManager(!!profile.hasReportingManager);
        setReportingManagerName(profile.reportingManagerName ?? null);
      })
      .catch(() => {});
  }, []);

  // Resuming a Draft ("Continue" on Other Reimbursements) jumps straight to
  // Bills; a request sent back for changes starts at step 1 with the
  // approver's note, same as the travel wizard.
  React.useEffect(() => {
    if (!draftIdToResume) return;
    fetchTravelRequest(draftIdToResume)
      .then((saved) => {
        const restored: Header = {
          country: saved.country || "",
          currency: saved.currency || "",
          purpose: saved.flightDetails || "",
          financeApproverId: saved.financeApproverIdAtSubmission || ""
        };
        setClaimType(saved.claimType || "");
        setHeader(restored);
        setSavedHeader(restored);
        setFinanceApproverNameAtSubmission(saved.financeApproverNameAtSubmission ?? null);
        setLinkedAdvanceId(saved.linkedAdvanceId || null);
        setDraftRequestId(saved.id);
        setDraftCreatedAt(saved.createdAt);
        setExpenses(saved.expenses);
        setDocuments(saved.documents || []);
        setFxOverrides(saved.fxOverrides || []);
        setSettlementTargetByCurrency(saved.settlementTargetByCurrency || {});
        if (saved.linkedAdvanceId) {
          fetchAdvance(saved.linkedAdvanceId)
            .then((a) => setLinkedAdvanceAmounts(a.amounts))
            .catch(() => {});
        }
        if (isResubmit && saved.resubmitComment) {
          setResubmitNote({
            stage: saved.resubmitRequestedBy === "Finance" ? "Finance" : "Manager",
            comment: saved.resubmitComment
          });
        }
        setStep(isResubmit ? CLAIM_TYPE_STEP : BILLS_STEP);
      })
      .catch(() => setError("Could not load this draft. It may have been discarded."))
      .finally(() => setResuming(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftIdToResume]);

  const setHeaderField = <K extends keyof Header>(key: K, value: Header[K]) =>
    setHeader((h) => ({ ...h, [key]: value }));

  const chooseCountry = (country: string) =>
    setHeader((h) => ({ ...h, country, currency: currencyForCountry(country) ?? h.currency }));

  const headerError = (): string | null => {
    if (!header.country) return "Select a country.";
    if (!header.purpose.trim()) return "Enter the purpose of this claim.";
    if (financeApprovers.length > 0 && !header.financeApproverId && !isResubmit) {
      return "Select a Finance Approver.";
    }
    return null;
  };
  const headerComplete = headerError() === null;
  const headerDirty =
    header.country !== savedHeader.country ||
    header.currency !== savedHeader.currency ||
    header.purpose.trim() !== savedHeader.purpose.trim() ||
    header.financeApproverId !== savedHeader.financeApproverId;

  const headerPayload = (): OtherRequestHeader => ({
    country: header.country,
    currency: header.currency,
    purpose: header.purpose.trim(),
    ...(isResubmit ? {} : { financeApproverId: header.financeApproverId || undefined })
  });

  // Bills need a request to attach to - created once the header is filled in.
  const createDraft = async () => {
    if (!claimType || !headerComplete) {
      setError(headerError());
      return;
    }
    setCreatingDraft(true);
    setError(null);
    try {
      const { request } = await createMyOtherRequest({
        claimType,
        ...headerPayload(),
        linkedAdvanceId: linkedAdvanceId || undefined
      });
      setDraftRequestId(request.id);
      setDraftCreatedAt(request.createdAt);
      setSavedHeader({ ...header, purpose: header.purpose.trim() });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not start your request - check your connection and try again.");
    } finally {
      setCreatingDraft(false);
    }
  };

  // Saves header edits made after the Draft exists. Returns false on failure.
  const saveHeaderIfDirty = async (): Promise<boolean> => {
    if (!draftRequestId || !headerDirty) return true;
    const problem = headerError();
    if (problem) {
      setError(problem);
      return false;
    }
    setSavingHeader(true);
    try {
      await updateMyOtherRequest(draftRequestId, headerPayload());
      setSavedHeader({ ...header, purpose: header.purpose.trim() });
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save your changes - check your connection and try again.");
      return false;
    } finally {
      setSavingHeader(false);
    }
  };

  // Links, swaps or unlinks the advance - told to the server straight away
  // once a Draft holds it, so it never stays consumed by mistake.
  const changeLinkedAdvance = async (advanceId: string | null, advance?: Advance): Promise<boolean> => {
    if (draftRequestId) {
      try {
        await setOtherLinkedAdvance(draftRequestId, advanceId);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not change the linked advance - check your connection and try again.");
        return false;
      }
    }
    setError(null);
    setLinkedAdvanceId(advanceId);
    setLinkedAdvanceAmounts(advance ? advance.amounts : null);
    // Carries the advance's own purpose over as a starting point - still a
    // free-text field the employee can change on the Bills step. Only fills
    // it in while Purpose is still blank, so it never overwrites something
    // already typed (e.g. swapping to a different advance later).
    if (advance?.flightDetails && !header.purpose.trim()) {
      setHeaderField("purpose", advance.flightDetails);
    }
    return true;
  };

  const chooseClaimType = (type: OtherClaimType) => {
    if (type === claimType) return;
    // An advance links only to its own claim type.
    if (linkedAdvanceId) void changeLinkedAdvance(null);
    setClaimType(type);
  };

  const validateStep = (): string | null => {
    if (step === CLAIM_TYPE_STEP && !claimType) return "Choose City travel or Miscellaneous.";
    if (step === ADVANCE_STEP && advanceToggleOn && !linkedAdvanceId) {
      return "Select which advance you received, or turn the toggle off.";
    }
    if (step === BILLS_STEP) {
      const problem = headerError();
      if (problem) return problem;
      if (!draftRequestId) return "Click \"Start adding bills\" to continue.";
      if (expenses.length === 0) return "Add at least one bill.";
    }
    return null;
  };

  const next = async () => {
    // Toasted, not the inline message this used to set - that rendered
    // inside the step's own scrollable content, so clicking Next from a
    // field near the top of a long step showed an error invisible until
    // scrolling all the way down to find it (same fix as NewRequest.tsx's
    // `next`). Draft-create/save/submit failures (setError elsewhere in
    // this file) are untouched - those still show inline where the user is
    // already looking, and are also what FinanceNoticeDialog's own `error`
    // prop displays.
    const problem = validateStep();
    if (problem) {
      toast.error(problem);
      return;
    }
    if (step === BILLS_STEP && !(await saveHeaderIfDirty())) return;
    setError(null);
    setStep((s) => Math.min(s + 1, REVIEW_STEP));
  };

  // Same unsaved-bill guard as the travel wizard: an un-added bill card or
  // an open bill edit is lost on leaving the Bills step.
  const hasUnsavedBillWork = step === BILLS_STEP && (unaddedBills > 0 || editingExpense);
  const blocker = useBlocker(hasUnsavedBillWork);
  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedBillWork) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedBillWork]);

  const goBack = () => {
    setError(null);
    setStep((s) => Math.max(s - 1, 1));
  };
  const [manualBackPending, setManualBackPending] = React.useState(false);
  const confirmBackOpen = manualBackPending || blocker.state === "blocked";
  const back = () => {
    if (hasUnsavedBillWork) {
      setManualBackPending(true);
      return;
    }
    goBack();
  };
  const confirmDiscardAndLeave = () => {
    if (manualBackPending) {
      setManualBackPending(false);
      goBack();
    }
    if (blocker.state === "blocked") blocker.proceed();
  };
  const cancelLeaving = () => {
    setManualBackPending(false);
    if (blocker.state === "blocked") blocker.reset();
  };

  const submit = async (ccEmails?: string[]) => {
    if (!draftRequestId) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isResubmit) {
        await resubmitMyOtherRequest(draftRequestId, {
          country: header.country,
          currency: header.currency,
          purpose: header.purpose.trim()
        });
        toast.success("Request resubmitted");
        navigate("/other-reimbursements");
        return;
      }
      if (!(await saveHeaderIfDirty())) return;
      const result = await finalizeMyRequest(draftRequestId, ccEmails, header.financeApproverId || undefined);
      toast.success(
        result.skippedToFinance
          ? "Submitted - you have no reporting manager on file, so this went straight to your Finance Approver."
          : "Request submitted"
      );
      setFinanceNoticeOpen(false);
      navigate("/other-reimbursements");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error(
          err.message ||
            "This request has already changed (maybe from another tab, or your approver acted on it) - taking you to Other Reimbursements to see its current status."
        );
        navigate("/other-reimbursements");
        return;
      }
      setError(
        err instanceof ApiError
          ? err.message
          : `Could not ${isResubmit ? "resubmit" : "submit"} this request - check your connection and try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmit = () => {
    setConfirmSubmitOpen(false);
    if (!isResubmit && !hasReportingManager) {
      setFinanceCc([]);
      setFinanceNoticeOpen(true);
      return;
    }
    void submit();
  };

  const financeApproverName =
    financeApprovers.find((a) => a.id === header.financeApproverId)?.displayName ||
    financeApproverNameAtSubmission ||
    header.financeApproverId;

  const ledgerSpansMultiple = spansMultipleCurrencies(expenses, linkedAdvanceAmounts ?? [], header.currency);
  // One currency only: the plain advance-vs-bills netting (the ledger covers
  // the multi-currency case).
  const netLines =
    linkedAdvanceAmounts && !ledgerSpansMultiple
      ? netPayableByCurrency(
          {
            claimMethod: "reimbursement",
            currency: header.currency,
            status: "Draft",
            settledAmount: null,
            estimatedAllowanceAmount: 0,
            expenses
          },
          linkedAdvanceAmounts
        )
      : [];

  const pageTitle = isResubmit ? "Edit Other Reimbursement" : "Create Other Reimbursement";

  if (resuming) {
    return (
      <PageContainer>
        <PageHeader title={pageTitle} description="Loading your draft…" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={pageTitle}
        description="City travel and miscellaneous expenses - add your bills and submit them for approval."
      />

      <WizardStepper steps={STEPS} currentStep={step} />

      {resubmitNote && (
        <div className="flex items-start gap-3 rounded-md border border-[hsl(var(--quadra-orange))] bg-[hsl(var(--quadra-orange)/0.1)] p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
          <div className="space-y-0.5">
            <p className="font-medium text-amber-700 dark:text-amber-400">
              Changes requested by {resubmitNote.stage === "Finance" ? "Finance" : "your reporting manager"}
            </p>
            <p className="whitespace-pre-wrap text-foreground">{resubmitNote.comment}</p>
          </div>
        </div>
      )}

      {/* No inner ScrollArea/height cap here - this wizard has no sidebar
          column to share height with (unlike NewRequest.tsx), so the card
          just grows with its content and the page itself scrolls. */}
      <Card className="flex min-w-0 flex-col">
        <CardContent className="flex flex-col gap-4 p-4 sm:p-6">
          <div>
            <div className="space-y-4">
              {step === CLAIM_TYPE_STEP && (
                <>
                  <Label>What are you claiming?</Label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {OTHER_CLAIM_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => chooseClaimType(t)}
                        // The claim type is fixed once the Draft exists -
                        // its bills were entered with this type's fields.
                        disabled={!!draftRequestId && t !== claimType}
                        className={`rounded-md border p-4 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:p-3 ${
                          claimType === t
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <p className="font-medium">{OTHER_CLAIM_TYPE_LABEL[t]}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{CLAIM_TYPE_HINT[t]}</p>
                      </button>
                    ))}
                  </div>
                  {draftRequestId && (
                    <p className="text-xs text-muted-foreground">
                      The claim type can't change once bills are being added. Discard this
                      draft from Other Reimbursements to start again with the other type.
                    </p>
                  )}
                </>
              )}

              {step === ADVANCE_STEP && claimType && (
                <OtherLinkAdvanceField
                  claimType={claimType}
                  linkedAdvanceId={linkedAdvanceId}
                  onChange={changeLinkedAdvance}
                  enabled={advanceToggleOn}
                  onToggleChange={setAdvanceToggleOn}
                />
              )}

              {step === BILLS_STEP && claimType && (
                <div className="space-y-4">
                  <div className="grid gap-4 rounded-md border p-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Country</Label>
                      <Combobox
                        value={header.country}
                        onChange={chooseCountry}
                        options={COUNTRY_OPTIONS}
                        placeholder="Select a country"
                        searchPlaceholder="Search countries…"
                      />
                      {header.currency && (
                        <p className="text-xs text-muted-foreground">
                          Bills default to {header.currency} - each bill can still use any currency.
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Finance Approver</Label>
                      {isResubmit ? (
                        <Input value={financeApproverName || "-"} disabled />
                      ) : (
                        <Combobox
                          value={header.financeApproverId}
                          onChange={(v) => setHeaderField("financeApproverId", v)}
                          options={financeApprovers.map((a) => ({
                            value: a.id,
                            label: a.displayName || a.email || a.id,
                            sublabel: a.email || undefined
                          }))}
                          placeholder="Select a Finance Approver"
                          searchPlaceholder="Search…"
                        />
                      )}
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="other-purpose">Purpose</Label>
                      <Textarea
                        id="other-purpose"
                        value={header.purpose}
                        onChange={(e) => setHeaderField("purpose", e.target.value)}
                        placeholder="e.g. Client visits in Coimbatore, week of 20 October"
                        maxLength={500}
                      />
                    </div>
                    {!draftRequestId && (
                      <div className="sm:col-span-2">
                        <Button onClick={() => void createDraft()} disabled={!headerComplete || creatingDraft}>
                          {creatingDraft && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                          Start adding bills
                        </Button>
                      </div>
                    )}
                  </div>

                  {draftRequestId && (
                    <Tabs defaultValue="bills" className="w-full min-w-0">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="bills" className="min-w-0 gap-1.5">
                          <Receipt className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">Bills &amp; Receipts</span>
                          {expenses.length > 0 && (
                            <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground">
                              {expenses.length}
                            </span>
                          )}
                        </TabsTrigger>
                        <TabsTrigger value="documents" className="min-w-0 gap-1.5">
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">
                            <span className="sm:hidden">Documents</span>
                            <span className="hidden sm:inline">Additional Documents</span>
                          </span>
                          {documents.length > 0 && (
                            <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/20 px-1.5 text-xs font-medium text-foreground">
                              {documents.length}
                            </span>
                          )}
                        </TabsTrigger>
                      </TabsList>
                      {/* Both force-mounted - see NewRequest.tsx: unmounting
                          would drop un-added bill cards on a tab switch. */}
                      <TabsContent value="bills" forceMount className="mt-4 min-w-0 data-[state=inactive]:hidden">
                        <ExpensesStep
                          travelRequestId={draftRequestId}
                          expenses={expenses}
                          onChanged={setExpenses}
                          defaultCurrency={header.currency}
                          claimMethod="reimbursement"
                          billFields={claimType}
                          onBusyChange={setExpensesBusy}
                          onPendingChange={setUnaddedBills}
                          onEditingChange={setEditingExpense}
                        />
                      </TabsContent>
                      <TabsContent value="documents" forceMount className="mt-4 min-w-0 data-[state=inactive]:hidden">
                        <DocumentsStep
                          travelRequestId={draftRequestId}
                          documents={documents}
                          onChanged={setDocuments}
                          onBusyChange={setDocumentsBusy}
                        />
                      </TabsContent>
                    </Tabs>
                  )}
                </div>
              )}

              {step === REVIEW_STEP && claimType && (
                <div className="space-y-3 text-sm">
                  <SummaryRow label="Designation" value={designation || "-"} />
                  {hasReportingManager && (
                    <SummaryRow label="Reporting Manager" value={reportingManagerName || "-"} />
                  )}
                  <SummaryRow label="Claim type" value={OTHER_CLAIM_TYPE_LABEL[claimType]} />
                  <SummaryRow label="Country" value={header.country || "-"} />
                  <SummaryRow label="Currency" value={header.currency || "-"} />
                  <SummaryRow label="Purpose" value={header.purpose || "-"} />
                  <SummaryRow label="Finance Approver" value={financeApproverName || "-"} />
                  {linkedAdvanceAmounts && (
                    <SummaryRow label="Advance received" value={formatAdvanceAmounts(linkedAdvanceAmounts)} />
                  )}
                  <SummaryRow
                    label="Bills uploaded"
                    value={
                      expenses.length > 0 ? (
                        <span className="inline-flex items-center gap-1.5">
                          {expenses.length}
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-primary"
                            onClick={() => setViewingBills(true)}
                            title="View uploaded bills"
                            aria-label="View uploaded bills"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ) : (
                        "0"
                      )
                    }
                  />
                  <NetPayableRows lines={netLines} party="you" />

                  {/* Editable before submitting - each row's settlement
                      currency and any FX override are saved on the Draft and
                      carried through to Review & approve, settlement and the
                      PDF. The advance converts at the request's created date
                      (there's no departure). */}
                  {expenses.length > 0 && (
                    <CurrencyConversionCard
                      travelRequestId={draftRequestId ?? undefined}
                      expenses={expenses}
                      advanceAmounts={linkedAdvanceAmounts ?? undefined}
                      fxOverrides={fxOverrides}
                      settlementTargetByCurrency={settlementTargetByCurrency}
                      defaultTargetCurrency={header.currency}
                      departureDate={draftCreatedAt}
                      onOverridesChanged={(updated) => {
                        setFxOverrides(updated.fxOverrides || []);
                        setSettlementTargetByCurrency(updated.settlementTargetByCurrency || {});
                      }}
                    />
                  )}
                </div>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          </div>

          <div className="sticky bottom-0 z-10 -mx-4 -mb-4 flex shrink-0 justify-between gap-3 rounded-b-lg border-t bg-card/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1 sm:mx-0 sm:mb-0 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:pt-2 sm:backdrop-blur-none">
            <Button
              variant="outline"
              onClick={step === CLAIM_TYPE_STEP ? () => navigate("/other-reimbursements") : back}
            >
              {step === CLAIM_TYPE_STEP ? "Cancel" : "Back"}
            </Button>
            {step < REVIEW_STEP ? (
              <Button
                onClick={() => void next()}
                disabled={
                  creatingDraft ||
                  savingHeader ||
                  (step === BILLS_STEP &&
                    (!draftRequestId ||
                      expensesBusy ||
                      documentsBusy ||
                      unaddedBills > 0 ||
                      editingExpense ||
                      expenses.length === 0))
                }
              >
                Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button onClick={() => setConfirmSubmitOpen(true)} disabled={submitting || !draftRequestId}>
                {submitting ? "Submitting…" : isResubmit ? "Resubmit request" : "Submit request"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmBackOpen}
        onOpenChange={(open) => {
          if (!open) cancelLeaving();
        }}
        title="Discard unsaved bill changes?"
        description={
          editingExpense
            ? "You're editing a bill. Leaving now discards that edit - it was never saved."
            : unaddedBills === 1
              ? "You have a bill that hasn't been added yet. Leaving now discards it."
              : `You have ${unaddedBills} bills that haven't been added yet. Leaving now discards all of them.`
        }
        confirmLabel="Discard and leave"
        destructive
        onConfirm={confirmDiscardAndLeave}
      />

      <ConfirmDialog
        open={confirmSubmitOpen}
        onOpenChange={setConfirmSubmitOpen}
        title={isResubmit ? "Resubmit this reimbursement?" : "Submit this reimbursement?"}
        description="Review the details above before continuing. Once submitted, it's routed for approval and can't be edited."
        confirmLabel={isResubmit ? "Resubmit request" : "Submit request"}
        pending={submitting}
        onConfirm={confirmSubmit}
      />

      {financeNoticeOpen && (
        <FinanceNoticeDialog
          open
          onClose={() => {
            setFinanceNoticeOpen(false);
            setFinanceCc([]);
          }}
          toName={financeApprovers.find((a) => a.id === header.financeApproverId)?.displayName ?? null}
          toEmail={financeApprovers.find((a) => a.id === header.financeApproverId)?.email ?? null}
          cc={financeCc}
          onCcChange={setFinanceCc}
          onSend={() => void submit(financeCc)}
          sending={submitting}
          error={error}
        />
      )}

      <Dialog open={viewingBills} onOpenChange={setViewingBills}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Uploaded bills</DialogTitle>
          </DialogHeader>
          {draftRequestId && claimType && (
            <BillsSummaryCard
              requestId={draftRequestId}
              expenses={expenses}
              currency={header.currency}
              estimatedAllowanceAmount={0}
              noLimit
              billFields={claimType}
            />
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
